import React, { useEffect, useRef, useState } from "react";
import { Globe, ChevronDown, Check } from "lucide-react";
import { useUser } from "../context/UserProvider";
import { auth, db } from "../lib/firebase";
import { doc, setDoc } from "firebase/firestore";
import { getLocalDeviceId } from "../utils/device";
import logo from "../assets/naim-logo.png";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGUAGES, changeLanguage, getCurrentLanguage } from "../i18n";

function formatDuration(ms) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

export default function DeviceLock() {
  const { deviceLock, uid } = useUser();
  const { t } = useTranslation();
  const [remaining, setRemaining] = useState(Math.max(0, (deviceLock?.until || 0) - Date.now()));
  const claimedRef = useRef(false);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(getCurrentLanguage());

  useEffect(() => {
    const id = setInterval(() => {
      setRemaining(Math.max(0, (deviceLock?.until || 0) - Date.now()));
    }, 1000);
    return () => clearInterval(id);
  }, [deviceLock?.until]);

  // When countdown reaches zero, attempt to claim ownership and start a new cooldown.
  useEffect(() => {
    if (!uid) return;
    if (remaining > 0) return;
    if (claimedRef.current) return;
    claimedRef.current = true;
    (async () => {
      try {
        const localId = getLocalDeviceId();
        const userRef = doc(db, "users", uid);
        await setDoc(
          userRef,
          { uniqueId: localId, uniqueIdUpdateDate: Date.now() },
          { merge: true }
        );
        // No manual navigate; ProtectedLayout will switch automatically
      } catch (e) {
        // Allow retry on next tick if the write fails.
        claimedRef.current = false;
      }
    })();
  }, [remaining, uid]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6 relative">
      {/* Language Selector - Top Right */}
      <div className="absolute top-4 right-4">
        <button
          onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
          className="flex items-center gap-2 bg-white px-3 py-2 rounded-full border border-gray-200 shadow-sm hover:shadow-md transition"
        >
          <Globe size={16} className="text-naimGreen" />
          <span className="text-naimGreen font-medium text-sm">
            {SUPPORTED_LANGUAGES.find(l => l.code === selectedLanguage)?.nativeName?.slice(0, 8) || 'EN'}
          </span>
          <ChevronDown size={14} className="text-naimGreen" />
        </button>
        {showLanguageDropdown && (
          <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-100 z-50 max-h-80 overflow-y-auto">
            {SUPPORTED_LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                onClick={() => {
                  setSelectedLanguage(lang.code);
                  changeLanguage(lang.code);
                  setShowLanguageDropdown(false);
                }}
                className={`w-full px-4 py-2 text-left flex justify-between items-center hover:bg-green-50 transition ${lang.code === selectedLanguage ? 'bg-green-50 text-naimGreen' : 'text-gray-700'}`}
              >
                <span>{lang.nativeName}</span>
                {lang.code === selectedLanguage && <Check size={16} className="text-naimGreen" />}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="w-full max-w-lg bg-white rounded-2xl shadow-md border border-gray-200 p-6 text-center">
        <img src={logo} alt="Naim" className="h-12 mx-auto mb-4" />
        <h1 className="text-xl font-semibold mb-2">{t('auth.deviceLockedTitle')}</h1>
        <p className="text-sm text-gray-600 mb-4">
          {t('auth.deviceLockedDescription')}
        </p>
        <div className="bg-gray-100 rounded-lg p-4 mb-4">
          <div className="text-gray-700 text-sm">{t('auth.timeRemaining')}</div>
          <div className="text-3xl font-mono font-semibold text-naimGreen mt-1">{formatDuration(remaining)}</div>
        </div>
        <button
          onClick={() => {
            auth.signOut().catch(() => { });
          }}
          className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-naimGreen text-white hover:bg-green-700"
        >
          {t('auth.signOut')}
        </button>
      </div>
    </div>
  );
}
